import React, { useState } from 'react';

import { Form, Formik } from 'formik';

import { Alert, Button, Modal } from '@shared/components/bootstrap';
import { FormikCheckboxFormGroup } from '@shared/components/fields/formik/formik_checkbox_form_group';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';

import { FormikHTMLFormGroup } from '@admin/components/fields/formik/formik_html_form_group';
import { client } from '@admin/libraries/apollo';
import { LogisticsLoadDocument, PaginatedLoadDocument, Status, useLoadRequestReviewedMutation } from '@admin/schema';

interface Props {
  load: { id: string; number: string; customerName: string };
  onClose: () => void;
}

type FormValues = {
  sendEmail: boolean;
  emailSubject: string;
  emailBody: string;
};

export const LoadRequestReviewedModal: React.FC<Props> = ({ load, onClose }) => {
  const defaultSubject = `Load Request Reviewed [${load.number}]`;
  const defaultBody = `
  <p>Hi <strong>${load.customerName}</strong>,</p>

  <p>We have reviewed your load request.</p>

  <p>You can view the status of your load and make any changes, if needed, using your account portal.</p>

  <p>If you need any additional help, don't hesitate to let us know.</p>

  <p>Thanks!</p>

  <p>The Clutter Team</p>
`;

  const [error, setError] = useState<string>();

  const [execute, { loading }] = useLoadRequestReviewedMutation({
    client,
    refetchQueries: [{ query: LogisticsLoadDocument, variables: { id: load.id } }, { query: PaginatedLoadDocument }],
    awaitRefetchQueries: true,
  });

  const onSubmit = async ({ emailSubject, emailBody, sendEmail }: FormValues) => {
    setError(undefined);
    const { data, errors } = await execute({
      variables: {
        input: {
          loadID: load.id,
          emailSubject: emailSubject,
          emailBody: emailBody,
          sendEmail: sendEmail,
        },
      },
    });

    if (data?.loadRequestReviewed?.status === Status.Ok) {
      onClose();
    } else {
      setError(data?.loadRequestReviewed?.error || errors?.[0]?.message || 'Unknown Error');
    }
  };

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Formik<FormValues>
          initialValues={{
            sendEmail: true,
            emailSubject: defaultSubject,
            emailBody: defaultBody,
          }}
          onSubmit={onSubmit}
        >
          {({ values }) => (
            <Form>
              <Modal.Header>
                <Modal.Title>Mark As Reviewed?</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {error && <Alert style="danger">{error}</Alert>}

                <FormikCheckboxFormGroup label="Notify Customer by Email" id="sendEmail" name="sendEmail" />
                {values.sendEmail && (
                  <>
                    <FormikInputFormGroup name="emailSubject" label="Subject" />
                    <FormikHTMLFormGroup name="emailBody" label="Body" />
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button disabled={loading} onClick={onClose} kind="secondary">
                  Cancel
                </Button>
                <Button loading={loading} kind="primary" type="submit">
                  Mark as Reviewed
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal.Content>
    </Modal>
  );
};
